import { useEffect, useMemo, useState } from 'react';

import type { CourseHomepage } from '@/features/courses/useCourse';
import { useCourse } from '@/features/courses/useCourse';

import type {
  AssignmentScore,
  GradebookAssignment,
  GradebookStudent,
} from './gradebookTypes';
import {
  buildCoachUnitKey,
  buildQuestionMetaKey,
  formatGradeValue,
  getSortableStudentLabel,
} from './gradebookTypes';
import { applyCoachScoresToAssignment, useSaveCoachScores } from './useSaveCoachScores';
import { applyEditedScoresToAssignment, useSaveGrade } from './useSaveGrade';
import { useRoster } from './useRoster';
import { useStudentGrades } from './useStudentGrades';

export type GradebookSortDirection = 'asc' | 'desc';

// Full credit for the assignment being edited: 'available' when some score is
// below its maximum; 'applied' just after full credit was given, until a score
// is changed by hand (the action then undoes it); 'complete' when every score
// is already at its maximum.
export type FullCreditState = 'applied' | 'available' | 'complete';

export interface GradebookEditingAssignment {
  componentUid: string;
  studentId: string;
}

function getScoreAssignments(course: CourseHomepage | undefined) {
  return (course?.gradebookAssignments ?? []).filter(
    (assignment) =>
      !assignment.isSectionDivider &&
      assignment.isVisibleToStudents &&
      (assignment.hasHomework ||
        assignment.hasPreLecture ||
        assignment.hasParticipation),
  );
}

/**
 * A student's totals, worked out from their score on each of the assignments
 * the gradebook shows now. Never the totals cached with their grades: those
 * were added up against the assignments of the moment the grades were fetched,
 * and after a question is removed the grades are fetched again before the
 * course is (issue #91: 8/15 under a card that said 8/10).
 */
function getStudentTotals(student: GradebookStudent, assignments: GradebookAssignment[]) {
  return assignments.reduce(
    (totals, assignment) => {
      const score = student.assignmentScores[assignment.componentUid];

      return {
        totalHomeworkPossible: totals.totalHomeworkPossible + assignment.homeworkMaxScore,
        totalHomeworkScore: totals.totalHomeworkScore + (score?.homeworkScore ?? 0),
        totalParticipationPossible:
          totals.totalParticipationPossible + assignment.participationMaxScore,
        totalParticipationScore:
          totals.totalParticipationScore + (score?.participationScore ?? 0),
        totalPreLecturePossible:
          totals.totalPreLecturePossible + assignment.preLectureMaxScore,
        totalPreLectureScore: totals.totalPreLectureScore + (score?.preLectureScore ?? 0),
      };
    },
    {
      totalHomeworkPossible: 0,
      totalHomeworkScore: 0,
      totalParticipationPossible: 0,
      totalParticipationScore: 0,
      totalPreLecturePossible: 0,
      totalPreLectureScore: 0,
    },
  );
}

function mergeStudents(
  rosterStudents: GradebookStudent[],
  gradeStudents: GradebookStudent[],
  assignments: GradebookAssignment[],
) {
  const gradeMap = new Map(gradeStudents.map((student) => [student.id, student]));
  const mergedRoster = rosterStudents.map((student) => {
    const gradedStudent = gradeMap.get(student.id);

    if (!gradedStudent) {
      return student;
    }

    return {
      ...student,
      ...gradedStudent,
      email: student.email || gradedStudent.email,
      firstName: student.firstName || gradedStudent.firstName,
      instituteId: student.instituteId || gradedStudent.instituteId,
      lastName: student.lastName || gradedStudent.lastName,
      profile: {
        ...gradedStudent.profile,
        firstName: student.profile.firstName || gradedStudent.profile.firstName,
        instituteId: student.profile.instituteId || gradedStudent.profile.instituteId,
        lastName: student.profile.lastName || gradedStudent.profile.lastName,
        preferredEmail:
          student.profile.preferredEmail || gradedStudent.profile.preferredEmail,
        preferredUsername:
          student.profile.preferredUsername ||
          gradedStudent.profile.preferredUsername,
        rawProfile: student.profile.rawProfile ?? gradedStudent.profile.rawProfile,
      },
    };
  });

  const rosterIds = new Set(mergedRoster.map((student) => student.id));
  const extraStudents = gradeStudents.filter((student) => !rosterIds.has(student.id));

  return [...mergedRoster, ...extraStudents].map((student) => ({
    ...student,
    ...getStudentTotals(student, assignments),
  }));
}

function getNumericScoreValue(student: GradebookStudent, column: string) {
  if (column === 'totalHomework') {
    return student.totalHomeworkScore;
  }

  if (column === 'totalPreLecture') {
    return student.totalPreLectureScore;
  }

  if (column === 'totalParticipation') {
    return student.totalParticipationScore;
  }

  const [kind, componentUid] = column.split(':');
  const assignment = student.assignmentScores[componentUid];

  if (!assignment) {
    return null;
  }

  if (kind === 'participation') {
    return assignment.participationScore;
  }

  return kind === 'preLecture'
    ? assignment.preLectureScore
    : assignment.homeworkScore;
}

function sortStudents(
  students: GradebookStudent[],
  sortColumn: string,
  sortDirection: GradebookSortDirection,
) {
  return [...students].sort((left, right) => {
    if (
      sortColumn === 'lastName' ||
      sortColumn === 'firstName' ||
      sortColumn === 'email' ||
      sortColumn === 'instituteId'
    ) {
      const leftLabel = getSortableStudentLabel(left, sortColumn).toLowerCase();
      const rightLabel = getSortableStudentLabel(right, sortColumn).toLowerCase();
      const comparison = leftLabel.localeCompare(rightLabel);
      return sortDirection === 'asc' ? comparison : comparison * -1;
    }

    const leftScore = getNumericScoreValue(left, sortColumn);
    const rightScore = getNumericScoreValue(right, sortColumn);

    if (leftScore === null && rightScore === null) {
      return getSortableStudentLabel(left, 'lastName').localeCompare(
        getSortableStudentLabel(right, 'lastName'),
      );
    }

    if (leftScore === null) {
      return 1;
    }

    if (rightScore === null) {
      return -1;
    }

    return sortDirection === 'asc'
      ? leftScore - rightScore
      : rightScore - leftScore;
  });
}

function createEditedScoreDraft(
  student: GradebookStudent,
  assignment: GradebookAssignment,
) {
  const assignmentScore = student.assignmentScores[assignment.componentUid] ?? null;
  const coachDraft = Object.fromEntries(
    (assignment.coachUnits ?? []).map((unit) => [
      buildCoachUnitKey(unit.uid),
      String(
        assignmentScore?.coachUnitScores?.find((score) => score.unitUid === unit.uid)?.score ?? 0,
      ),
    ]),
  );

  return assignment.sections.reduce<Record<string, string>>((draft, section) => {
    section.homeworkQuestions.forEach((question) => {
      const currentQuestion =
        assignmentScore?.questionScores.find(
          (candidate) =>
            candidate.index === question.index &&
            candidate.kind === question.kind &&
            candidate.sectionId === question.sectionId,
        ) ?? null;
      draft[buildQuestionMetaKey(question)] = String(currentQuestion?.score ?? 0);
    });
    section.preLectureQuestions.forEach((question) => {
      const currentQuestion =
        assignmentScore?.preLectureQuestionScores.find(
          (candidate) =>
            candidate.index === question.index &&
            candidate.kind === question.kind &&
            candidate.sectionId === question.sectionId,
        ) ?? null;
      draft[buildQuestionMetaKey(question)] = String(currentQuestion?.score ?? 0);
    });

    return draft;
  }, coachDraft);
}

// Scores are compared to their maxima with this much give, so a maximum that
// carries float noise (a sum of a multi-part question's parts) is still met.
const SCORE_TOLERANCE = 1e-6;

/**
 * A maximum as it is written into a score input: rounded to 6 decimals, so a
 * sum of parts never shows float noise such as 0.30000000000000004. The server
 * stores the exact maximum (it treats a rounding hair above it as the maximum).
 */
export function formatFullCreditValue(max: number): string {
  return String(Math.round(max * 1e6) / 1e6);
}

// Every score the card edits for an assignment, with its maximum: its assigned
// questions (the card shows no others) and its listed coach units.
function editableMaxima(assignment: GradebookAssignment): Array<[string, number]> {
  const entries: Array<[string, number]> = [];
  assignment.sections.forEach((section) => {
    [...section.homeworkQuestions, ...section.preLectureQuestions]
      .filter((question) => question.isAssigned)
      .forEach((question) => entries.push([buildQuestionMetaKey(question), question.maxScore]));
  });
  (assignment.coachUnits ?? []).forEach((unit) => {
    entries.push([buildCoachUnitKey(unit.uid), unit.points]);
  });
  return entries;
}

/** The draft with every editable score set to its maximum. */
export function fullCreditDraft(
  assignment: GradebookAssignment,
  draft: Record<string, string>,
): Record<string, string> {
  const next = { ...draft };
  editableMaxima(assignment).forEach(([key, max]) => {
    next[key] = formatFullCreditValue(max);
  });
  return next;
}

function isAtFullCredit(assignment: GradebookAssignment, draft: Record<string, string>) {
  const maxima = editableMaxima(assignment);
  return (
    maxima.length > 0 &&
    maxima.every(([key, max]) => parseDraftValue(draft[key], 0) >= max - SCORE_TOLERANCE)
  );
}

function parseDraftValue(value: string | undefined, fallback = 0) {
  const trimmed = value?.trim() ?? '';

  if (trimmed.length === 0) {
    return fallback;
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function scoreError(value: string | undefined, maxScore: number): string | null {
  const trimmed = value?.trim() ?? '';

  if (trimmed.length === 0) {
    return 'Enter a score.';
  }

  const parsed = Number(trimmed);

  if (!Number.isFinite(parsed)) {
    return 'Enter a valid number.';
  }

  if (parsed < 0 || parsed > maxScore + SCORE_TOLERANCE) {
    return `Score must be between 0 and ${formatGradeValue(maxScore)}.`;
  }

  return null;
}

function getEditedScoreErrors(
  assignment: GradebookAssignment,
  editedScores: Record<string, string>,
) {
  const errors: Record<string, string> = {};

  assignment.sections.forEach((section) => {
    [...section.homeworkQuestions, ...section.preLectureQuestions].forEach((question) => {
      const key = buildQuestionMetaKey(question);
      const error = scoreError(editedScores[key], question.maxScore);

      if (error) {
        errors[key] = error;
      }
    });
  });
  (assignment.coachUnits ?? []).forEach((unit) => {
    const key = buildCoachUnitKey(unit.uid);
    const error = scoreError(editedScores[key], unit.points);

    if (error) {
      errors[key] = error;
    }
  });

  return errors;
}

// The coach units whose drafted score differs from the stored one (a unit never
// scored counts as 0), as unit uid → score: only these are saved.
function getChangedCoachScores(
  student: GradebookStudent,
  assignment: GradebookAssignment,
  editedScores: Record<string, string>,
) {
  const assignmentScore = student.assignmentScores[assignment.componentUid] ?? null;
  const changed: Record<string, number> = {};

  (assignment.coachUnits ?? []).forEach((unit) => {
    const current =
      assignmentScore?.coachUnitScores?.find((score) => score.unitUid === unit.uid)?.score ?? 0;
    const next = parseDraftValue(editedScores[buildCoachUnitKey(unit.uid)], current);

    if (next !== current) {
      changed[unit.uid] = next;
    }
  });

  return changed;
}

// The questions whose drafted score differs from the stored one (a question
// never scored counts as 0), as question meta key → score: only these are
// saved, and each becomes instructor-adjusted.
function getChangedQuestionScores(
  student: GradebookStudent,
  assignment: GradebookAssignment,
  editedScores: Record<string, string>,
) {
  const assignmentScore = student.assignmentScores[assignment.componentUid] ?? null;
  const changed: Record<string, number> = {};

  assignment.sections.forEach((section) => {
    [...section.homeworkQuestions, ...section.preLectureQuestions].forEach((question) => {
      const key = buildQuestionMetaKey(question);
      const scores =
        question.kind === 'preLecture'
          ? assignmentScore?.preLectureQuestionScores
          : assignmentScore?.questionScores;
      const current =
        scores?.find(
          (candidate) =>
            candidate.index === question.index &&
            candidate.kind === question.kind &&
            candidate.sectionId === question.sectionId,
        )?.score ?? 0;
      const next = parseDraftValue(editedScores[key], current);

      if (next !== current) {
        changed[key] = next;
      }
    });
  });

  return changed;
}

function draftedCoachScores(assignment: GradebookAssignment, editedScores: Record<string, string>) {
  return Object.fromEntries(
    (assignment.coachUnits ?? []).map((unit) => [
      unit.uid,
      parseDraftValue(editedScores[buildCoachUnitKey(unit.uid)], 0),
    ]),
  );
}

function hasEditedValuesChanged(
  student: GradebookStudent,
  assignment: GradebookAssignment,
  editedScores: Record<string, string>,
) {
  const assignmentScore = student.assignmentScores[assignment.componentUid] ?? null;

  if (Object.keys(getChangedCoachScores(student, assignment, editedScores)).length > 0) {
    return true;
  }

  return assignment.sections.some((section) =>
    [...section.homeworkQuestions, ...section.preLectureQuestions].some((question) => {
      const key = buildQuestionMetaKey(question);
      const currentQuestion =
        question.kind === 'preLecture'
          ? assignmentScore?.preLectureQuestionScores.find(
              (candidate) =>
                candidate.index === question.index &&
                candidate.kind === question.kind &&
                candidate.sectionId === question.sectionId,
            ) ?? null
          : assignmentScore?.questionScores.find(
              (candidate) =>
                candidate.index === question.index &&
                candidate.kind === question.kind &&
                candidate.sectionId === question.sectionId,
            ) ?? null;

      return parseDraftValue(editedScores[key], 0) !== (currentQuestion?.score ?? 0);
    }),
  );
}

export function useGradebook(courseId: string | undefined) {
  const [sortColumn, setSortColumnState] = useState('lastName');
  const [sortDirection, setSortDirection] = useState<GradebookSortDirection>('asc');
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [editingAssignment, setEditingAssignment] =
    useState<GradebookEditingAssignment | null>(null);
  const [editedScores, setEditedScores] = useState<Record<string, string>>({});
  // The draft as it was just before full credit was given, for Undo. Dropped
  // as soon as a score is changed by hand, or the draft is saved or left.
  const [fullCreditUndo, setFullCreditUndo] = useState<Record<string, string> | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const courseQuery = useCourse(courseId);
  const assignments = useMemo(
    () => getScoreAssignments(courseQuery.data),
    [courseQuery.data],
  );
  const rosterQuery = useRoster(courseId, {
    enabled: courseQuery.data?.userRole === 'instructor',
  });
  const rosterIds = useMemo(
    () => (rosterQuery.data?.students ?? []).map((student) => student.id),
    [rosterQuery.data?.students],
  );
  const gradesQuery = useStudentGrades({
    assignments,
    componentIds: assignments.map((assignment) => assignment.componentUid),
    courseId,
    enabled:
      courseQuery.data?.userRole === 'instructor' &&
      rosterQuery.isSuccess,
    rosterIds,
    studentCount: rosterQuery.data?.studentCount ?? 0,
  });
  // Placeholder rows are not grades: still loading until every batch is in.
  const isLoadingGrades =
    !gradesQuery.isError &&
    (gradesQuery.isPending || gradesQuery.data?.isComplete !== true);
  const saveGrade = useSaveGrade(courseId);
  const saveCoachScores = useSaveCoachScores(courseId);

  const mergedStudents = useMemo(
    () =>
      mergeStudents(
        rosterQuery.data?.students ?? [],
        gradesQuery.data?.students ?? [],
        assignments,
      ),
    [assignments, gradesQuery.data?.students, rosterQuery.data?.students],
  );
  const students = useMemo(
    () => sortStudents(mergedStudents, sortColumn, sortDirection),
    [mergedStudents, sortColumn, sortDirection],
  );
  const selectedStudent = useMemo(
    () =>
      mergedStudents.find((student) => student.id === selectedStudentId) ?? null,
    [mergedStudents, selectedStudentId],
  );
  const activeAssignment = useMemo(
    () =>
      editingAssignment
        ? assignments.find(
            (assignment) => assignment.componentUid === editingAssignment.componentUid,
          ) ?? null
        : null,
    [assignments, editingAssignment],
  );
  const scoreErrors = useMemo(
    () =>
      activeAssignment ? getEditedScoreErrors(activeAssignment, editedScores) : {},
    [activeAssignment, editedScores],
  );
  const hasUnsavedChanges =
    Boolean(selectedStudent && activeAssignment) &&
    hasEditedValuesChanged(
      selectedStudent!,
      activeAssignment!,
      editedScores,
    );

  useEffect(() => {
    if (
      selectedStudentId &&
      !mergedStudents.some((student) => student.id === selectedStudentId)
    ) {
      setSelectedStudentId(null);
    }
  }, [mergedStudents, selectedStudentId]);

  const setSortColumn = (column: string) => {
    setSortColumnState((currentColumn) => {
      if (currentColumn === column) {
        setSortDirection((currentDirection) =>
          currentDirection === 'asc' ? 'desc' : 'asc',
        );
        return currentColumn;
      }

      setSortDirection('asc');
      return column;
    });
  };

  const selectStudent = (studentId: string | null) => {
    setSelectedStudentId(studentId);
  };

  const startEditing = async (studentId: string, componentUid: string) => {
    const student = mergedStudents.find((candidate) => candidate.id === studentId);
    const assignment = assignments.find(
      (candidate) => candidate.componentUid === componentUid,
    );

    if (!student || !assignment) {
      return false;
    }

    if (
      editingAssignment &&
      (editingAssignment.studentId !== studentId ||
        editingAssignment.componentUid !== componentUid) &&
      hasUnsavedChanges
    ) {
      const shouldSaveBeforeSwitch = window.confirm(
        'Save current grade changes before switching assignments?',
      );

      if (shouldSaveBeforeSwitch) {
        const didSave = await saveEdits();

        if (!didSave) {
          return false;
        }
      } else {
        const shouldDiscard = window.confirm(
          'Discard current grade changes and switch assignments?',
        );

        if (!shouldDiscard) {
          return false;
        }
      }
    }

    setSelectedStudentId(studentId);
    setEditingAssignment({ componentUid, studentId });
    setEditedScores(createEditedScoreDraft(student, assignment));
    setFullCreditUndo(null);
    setSaveError(null);
    return true;
  };

  const updateScore = (questionKey: string, value: string) => {
    setFullCreditUndo(null);
    setEditedScores((currentScores) => ({
      ...currentScores,
      [questionKey]: value,
    }));
  };

  const giveFullCredit = () => {
    if (!activeAssignment) {
      return;
    }
    setFullCreditUndo(editedScores);
    setEditedScores(fullCreditDraft(activeAssignment, editedScores));
  };

  const undoFullCredit = () => {
    if (!fullCreditUndo) {
      return;
    }
    setEditedScores(fullCreditUndo);
    setFullCreditUndo(null);
  };

  const fullCreditState: FullCreditState | null = activeAssignment
    ? fullCreditUndo
      ? 'applied'
      : isAtFullCredit(activeAssignment, editedScores)
        ? 'complete'
        : 'available'
    : null;

  const cancelEditing = () => {
    if (selectedStudent && activeAssignment && hasUnsavedChanges) {
      const shouldDiscard = window.confirm('Discard unsaved grade changes?');

      if (!shouldDiscard) {
        return false;
      }
    }

    setEditingAssignment(null);
    setEditedScores({});
    setFullCreditUndo(null);
    setSaveError(null);
    return true;
  };

  const saveEdits = async () => {
    if (!selectedStudent || !activeAssignment || !courseId) {
      return false;
    }

    const validationErrors = getEditedScoreErrors(activeAssignment, editedScores);

    if (Object.keys(validationErrors).length > 0) {
      setSaveError('Fix the highlighted score values before saving.');
      return false;
    }

    setSaveError(null);

    // A coach card: only the units whose score changed go to the coach
    // endpoint, where each becomes instructor-adjusted.
    if (activeAssignment.hasParticipation) {
      const changed = getChangedCoachScores(selectedStudent, activeAssignment, editedScores);

      if (Object.keys(changed).length === 0) {
        setEditingAssignment(null);
        setEditedScores({});
        setFullCreditUndo(null);
        return true;
      }

      try {
        await saveCoachScores.mutateAsync({
          assignment: activeAssignment,
          courseId,
          scores: changed,
          student: selectedStudent,
        });
        setEditingAssignment(null);
        setEditedScores({});
        setFullCreditUndo(null);
        return true;
      } catch {
        setSaveError('Unable to save the coach scores right now. Please try again.');
        return false;
      }
    }

    // Question scores: only the questions whose score changed are saved, and
    // each becomes instructor-adjusted.
    const changedQuestions = getChangedQuestionScores(
      selectedStudent,
      activeAssignment,
      editedScores,
    );

    if (Object.keys(changedQuestions).length === 0) {
      setEditingAssignment(null);
      setEditedScores({});
      setFullCreditUndo(null);
      return true;
    }

    try {
      await saveGrade.mutateAsync({
        assignment: activeAssignment,
        courseId,
        editedQuestionScores: changedQuestions,
        student: selectedStudent,
      });
      setEditingAssignment(null);
      setEditedScores({});
      setFullCreditUndo(null);
      return true;
    } catch {
      // Never the server's text: it is not written for instructors.
      setSaveError('Unable to save the grade changes right now. Please try again.');
      return false;
    }
  };

  const getDisplayedAssignmentScore = (
    studentId: string,
    componentUid: string,
  ): AssignmentScore | null => {
    const student = mergedStudents.find((candidate) => candidate.id === studentId);
    const assignment = assignments.find(
      (candidate) => candidate.componentUid === componentUid,
    );

    if (!student || !assignment) {
      return null;
    }

    const currentScore = student.assignmentScores[componentUid] ?? null;

    if (
      editingAssignment?.studentId === studentId &&
      editingAssignment.componentUid === componentUid &&
      assignment.hasParticipation
    ) {
      return applyCoachScoresToAssignment(
        assignment,
        currentScore,
        draftedCoachScores(assignment, editedScores),
        false,
      );
    }

    if (
      editingAssignment?.studentId === studentId &&
      editingAssignment.componentUid === componentUid
    ) {
      return applyEditedScoresToAssignment(
        assignment,
        currentScore,
        Object.fromEntries(
          Object.entries(editedScores).map(([key, value]) => [
            key,
            parseDraftValue(value, 0),
          ]),
        ),
      );
    }

    return currentScore;
  };

  return {
    assignments,
    course: courseQuery.data,
    courseQuery,
    editedScores,
    editingAssignment,
    fullCreditState,
    getDisplayedAssignmentScore,
    giveFullCredit,
    gradesQuery,
    hasUnsavedChanges,
    // A failed load is neither loading nor loaded: the pages say it failed
    // and offer to try again. Shown as loaded, the roster's rows read as zeros
    // and exported as them (review of PR 110).
    gradesComplete: !gradesQuery.isError && gradesQuery.data?.isComplete === true,
    gradesError: gradesQuery.isError ? gradesQuery.error : null,
    gradesFailed: gradesQuery.isError,
    isLoadingGrades,
    isLoadingRoster: rosterQuery.isPending,
    retryGrades: () => {
      void gradesQuery.refetch();
    },
    isSaving: saveGrade.isPending || saveCoachScores.isPending,
    loadingProgress:
      gradesQuery.data && gradesQuery.data.total > 0
        ? {
            loaded: gradesQuery.data.loaded,
            total: gradesQuery.data.total,
          }
        : null,
    rosterQuery,
    saveEdits,
    // Only the page's own wording: a failed save's raw error text is never shown.
    saveError,
    // The enrolled-student count is known from the roster response even before the
    // (paginated) per-student grade rows finish loading. Use it to tell apart a
    // genuinely empty course from one whose roster is still being hydrated.
    studentCount: rosterQuery.data?.studentCount ?? 0,
    scoreErrors,
    selectedStudent,
    selectedStudentId,
    selectStudent,
    setSortColumn,
    sortColumn,
    sortDirection,
    startEditing,
    students,
    undoFullCredit,
    updateScore,
    cancelEditing,
  };
}
